import { useCallback, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import type RjsfForm from '@rjsf/core';

interface UseGuardedSaveOptions {
  formRef: RefObject<RjsfForm | null>;
  onSubmit: (formData: Record<string, unknown>) => Promise<unknown>;
  /** Where the header's back button lands once leaving is allowed. */
  backPath: string;
  isSaving: boolean;
  /**
   * Lets Save submit a form the user has not touched. A section being created holds nothing but
   * schema defaults, which are already valid content, so Save has to be live from the start.
   * Deliberately separate from the dirty flag: an untouched form has no *changes* to lose, so
   * Back still leaves without asking.
   */
  allowPristineSave?: boolean;
  /** Runs only after the section is actually persisted — e.g. refreshing a live preview. */
  onSaved?: () => void;
}

interface UnsavedChangesDialogProps {
  open: boolean;
  isSaving: boolean;
  onSaveAndLeave: () => void;
  onLeaveWithoutSaving: () => void;
  onCancel: () => void;
}

export interface GuardedSave {
  /** The user edited something: drives the back-navigation guard and the stale-preview hint. */
  isDirty: boolean;
  /** Whether Save has anything to submit — edits, or the untouched defaults of a new section. */
  canSave: boolean;
  markDirty: () => void;
  /** Resolves to whether the form was persisted, so callers can decide about navigating. */
  save: () => Promise<boolean>;
  /** Feeds useNavigationHeader's onBeforeNavigate: false keeps the user in the editor. */
  confirmNavigation: () => boolean;
  dialogProps: UnsavedChangesDialogProps;
}

// Shared by the page section and header/footer editors: both gate Save on unsaved edits and both
// have to confirm before the back button discards them, so the flow lives in one place.
export function useGuardedSave({
  formRef,
  onSubmit,
  backPath,
  isSaving,
  allowPristineSave = false,
  onSaved,
}: UseGuardedSaveOptions): GuardedSave {
  const navigate = useNavigate();
  const [isDirty, setIsDirty] = useState(false);
  // Spent by the first successful save: the section exists from then on, so its defaults are no
  // longer pending content.
  const [isPristineSavable, setIsPristineSavable] = useState(allowPristineSave);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);

  // A rejected save leaves the form dirty so the user can retry, and stops "Save and leave" from
  // navigating away from work that never landed. The mutation raises its own error snackbar.
  const save = useCallback(async () => {
    const form = formRef.current;
    if (!form || !form.validateForm()) return false;

    try {
      await onSubmit(form.state.formData as Record<string, unknown>);
    } catch {
      return false;
    }

    setIsDirty(false);
    setIsPristineSavable(false);
    onSaved?.();
    return true;
  }, [formRef, onSubmit, onSaved]);

  const leave = useCallback(() => navigate(backPath, { replace: true }), [navigate, backPath]);

  const saveAndLeave = useCallback(async () => {
    const saved = await save();
    // Closed either way: rjsf puts validation errors on the form, which the dialog would cover.
    setIsLeaveDialogOpen(false);
    if (saved) leave();
  }, [save, leave]);

  return {
    isDirty,
    canSave: isDirty || isPristineSavable,
    markDirty: () => setIsDirty(true),
    save,
    confirmNavigation: () => {
      if (!isDirty) return true;
      setIsLeaveDialogOpen(true);
      return false;
    },
    dialogProps: {
      open: isLeaveDialogOpen,
      isSaving,
      onSaveAndLeave: () => {
        saveAndLeave();
      },
      onLeaveWithoutSaving: () => {
        setIsLeaveDialogOpen(false);
        leave();
      },
      onCancel: () => setIsLeaveDialogOpen(false),
    },
  };
}
